import classNames from 'classnames';
import { isValidElement, type ReactNode, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import type { RouteNavConfig } from '@revelio/auth';
import { PageTitles, useTourStore } from '@revelio/core';

import {
  getPageConfigForPath,
  type PublicCustomDashPath,
} from '../../../CustomDash/utils/page-config-lookup';
import {
  isAnalyticsEntity,
  resolveEntityLandingPath,
} from '../../../CustomDash/utils/revelio-primary-filter-entity-switch';
import { getLastViewedTab } from '../../../CustomDash/utils/tab-state';
import styles from './ExpandedNav.module.css';
import { NavChevron } from './NavChevron';

// A splat route ('data-dictionary/*') links to and matches on its base path.
const normalizePath = (path?: string): string | undefined => {
  if (!path) return undefined;
  const base = path.replace(/\/\*$/, '');
  return base.startsWith('/') ? base : `/${base}`;
};

const firstSegment = (path?: string): string | undefined =>
  normalizePath(path)?.split('/').filter(Boolean)[0];

const getVisibleChildren = (item: RouteNavConfig): RouteNavConfig[] =>
  (item.children ?? []).filter(
    (child) => !child.excludeFromSideNavButtons && child.path
  );

const childTargetPath = (
  child: RouteNavConfig,
  sectionName: string,
  pages: ReadonlyArray<{ id: string }>
): string | undefined => {
  if (!child.path) return undefined;

  const defaultPath = resolveEntityLandingPath(child.path);
  if (sectionName !== PageTitles.DASHBOARD_NAV) return defaultPath;

  const entity = firstSegment(child.path);
  if (!entity || !isAnalyticsEntity(entity)) return defaultPath;

  const lastViewed = getLastViewedTab(entity);
  if (!lastViewed) return defaultPath;

  // Guard against restoring a custom page that has since been deleted.
  const pageId = lastViewed.split('/')[1];
  const isLocalTemplate = !!getPageConfigForPath(
    lastViewed as PublicCustomDashPath
  );
  const isExistingPage = pages.some((p) => p.id === pageId);
  if (!isLocalTemplate && !isExistingPage) return defaultPath;

  // The removed Overview page keeps its /…/summary route and local template, so
  // a stored last-viewed can still be `<entity>/summary`. Re-resolve it so the
  // rail never restores a page the layout has dropped.
  return resolveEntityLandingPath(lastViewed);
};

const customDashSectionFor = (entity: string): string | undefined => {
  if (isAnalyticsEntity(entity)) return PageTitles.DASHBOARD_NAV;
  if (entity === 'report') return PageTitles.REPORTS_NAV;
  return undefined;
};

export interface ExpandedNavProps {
  className?: string;
  items: RouteNavConfig[];
  pages?: ReadonlyArray<{ id: string }>;
  footer?: ReactNode;
}

export const ExpandedNav = ({
  className,
  items,
  pages = [],
  footer,
}: ExpandedNavProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState<string | null>(null);

  // While the guided tour runs it owns which section is open — the cursor sits
  // on the Joyride tooltip (portalled to <body>), so the hover/click state
  // would fight it. `isNavPinned` holds the rail open without a hovering pointer.
  const isTour = useTourStore((s) => s.isTour);
  const isNavPinned = useTourStore((s) => s.isNavPinned);
  const tourNavSection = useTourStore((s) => s.tourNavSection);
  const activeSection = isTour ? tourNavSection : openSection;

  const pathMatches = (path?: string): boolean => {
    const full = normalizePath(path);
    if (!full) return false;
    return (
      location.pathname === full || location.pathname.startsWith(`${full}/`)
    );
  };

  const isChildActive = (
    child: RouteNavConfig,
    siblings: RouteNavConfig[],
    customDashEntity?: string
  ): boolean => {
    if (pathMatches(child.path)) return true;
    const segment = firstSegment(child.path);
    if (!segment) return false;
    if (segment === customDashEntity) return true;
    return siblings.some(
      (sibling) =>
        sibling.excludeFromSideNavButtons &&
        firstSegment(sibling.path) === segment &&
        pathMatches(sibling.path)
    );
  };

  const sections = items.filter(
    (item) => getVisibleChildren(item).length > 0 || item.path
  );

  const customDash = (() => {
    const matchesNavPath = items.some(
      (item) =>
        pathMatches(item.path) ||
        (item.children ?? []).some((child) => pathMatches(child.path))
    );
    if (matchesNavPath) return undefined;
    const entity = firstSegment(location.pathname);
    if (!entity) return undefined;
    const section = customDashSectionFor(entity);
    return section ? { entity, section } : undefined;
  })();

  return (
    <nav
      className={classNames(styles.root, className)}
      aria-label="Main navigation"
      data-testid="expanded-nav"
      data-tour-pinned={isNavPinned ? 'true' : undefined}
      onMouseLeave={() => {
        if (isTour) return;
        setOpenSection(null);
      }}
    >
      {sections.map((item) => {
        const name = String(item.name ?? '');
        const children = getVisibleChildren(item);
        const allChildren = item.children ?? [];
        const hasChildren = children.length > 0;
        const ownsCustomDash = customDash?.section === name;
        const isActiveSection =
          pathMatches(item.path) ||
          allChildren.some((child) => pathMatches(child.path)) ||
          ownsCustomDash;
        const isOpen = hasChildren && activeSection === name;
        const iconNode = isValidElement(item.icon) ? item.icon : null;

        return (
          <div
            key={name}
            className={classNames(styles.section, isOpen && styles.sectionOpen)}
            // Joyride spotlights the whole section (header + expanded children),
            // not just the header button, so the highlight grows with it as the
            // tour opens the section.
            data-tour-nav-section={name}
          >
            {item.divider && <div className={styles.divider} />}
            <button
              type="button"
              className={classNames(
                styles.header,
                isActiveSection && styles.headerActive
              )}
              aria-expanded={hasChildren ? isOpen : undefined}
              data-nav-section={name}
              data-testid={`expanded-nav-section-${name}`}
              onClick={() => {
                if (hasChildren) {
                  setOpenSection(openSection === name ? '' : name);
                  return;
                }
                const target = normalizePath(item.path);
                if (target) navigate(target);
              }}
            >
              <span className={styles.iconBox}>{iconNode}</span>
              <span className={styles.label}>{name}</span>
              {hasChildren && (
                <span
                  className={classNames(
                    styles.chevron,
                    isOpen && styles.chevronOpen
                  )}
                >
                  <NavChevron />
                </span>
              )}
            </button>
            {hasChildren && (
              <div className={styles.childrenWrap}>
                <div className={styles.children}>
                  {children.map((child) => (
                    <NavLink
                      key={String(child.name)}
                      to={
                        normalizePath(childTargetPath(child, name, pages)) ?? ''
                      }
                      className={classNames(
                        styles.childLink,
                        isChildActive(
                          child,
                          allChildren,
                          ownsCustomDash ? customDash?.entity : undefined
                        ) && styles.childActive
                      )}
                      data-testid={`expanded-nav-child-${name}-${String(child.name)}`}
                      data-nav-child={String(child.name)}
                    >
                      {String(child.name)}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {footer && <div className={styles.footer}>{footer}</div>}
    </nav>
  );
};
